import { Injectable, inject } from '@angular/core';
import type { CraftXGritProductEntry } from '@core-mkt/interfaces/craft-xgrit-product-entry';
import { CouponAggregationService } from '@core-mkt/services/coupon-aggregation/coupon-aggregation.service';
import { CouponQueryResolverService } from '@core-mkt/services/coupon-query-resolver/coupon-query-resolver.service';
import { CouponUseCases } from '@core-mkt/services/coupon-query-resolver/coupon-use-cases';
import { EnvService } from '@core-mkt/services/env/env.service';

/** The non-prod label in a checkout host, e.g. `ecom-stage` in `checkout.ecom-stage.aceable.com`. */
const NON_PROD_HOST_LABEL = /\.ecom-(dev|stage)\./;

/** Non-prod checkout hosts that do not follow the `ecom-{dev|stage}` pattern, mapped to their prod host. */
const NON_PROD_TO_PROD_CHECKOUT_HOST: Record<string, string> = {
  'paymentstest.aarpdriversafety.org': 'payments.aarpdriversafety.org',
};

/**
 * Service for building checkout URLs with proper coupon parameter handling.
 * Provides smart vc/hc deduplication to prevent duplicate coupons.
 */
@Injectable({
  providedIn: 'root',
})
export class CheckoutUrlBuilderService {
  private couponAggregationService = inject(CouponAggregationService);
  private couponQueryResolverService = inject(CouponQueryResolverService);
  private envCheckoutHosts = this.resolveEnvCheckoutHosts(inject(EnvService).get.xgritCheckoutUrl);

  /**
   * Builds a checkout URL with proper coupon parameter handling.
   *
   * Two modes:
   *  - **Merge mode** (default, when `replaceCoupons` is empty/undefined): aggregates
   *    existing URL coupon params, environment coupons (banner/email/URL params via
   *    CouponAggregationService), and any per-card craft coupons, deduped via Set.
   *  - **Replace mode** (when `replaceCoupons` is non-empty): an active banner
   *    `overwriteCoupon` is being applied, so it overwrites existing URL coupons and
   *    the environment-coupon aggregation. **Per-card Craft `xgritCoupon`s still ride
   *    through** so each product's Offer coupon is preserved alongside the banner code.
   *    Drops `vc`. The replace param is caller-driven (component knows whether the sale
   *    is active); when the array is empty, falls through to merge mode.
   *
   * @param baseUrl - The base checkout URL to modify
   * @param craftProducts - Optional per-card Craft product entries whose xgritCoupon
   *                       should be included in the final coupon set (both modes)
   * @param replaceCoupons - Optional replacement coupons. When non-empty, switches the
   *                       function to replace mode; the resulting URL has these coupons
   *                       (unioned with per-card craft coupons) in its `hc` param.
   * @param preComputedEnvironmentCoupons - Optional pre-aggregated environment coupons
   *                       (banner/email/URL params). When provided, skips the internal
   *                       `collectAllCoupons()` call — useful for callers (e.g.,
   *                       ProductCardComponent) that resolve checkout URLs for many
   *                       cards in a single render and want to avoid the per-call cost
   *                       of URL-param parsing and cookie reads. Per-card craft coupons
   *                       are still extracted from `craftProducts` (cheap, no I/O).
   * @returns The enhanced checkout URL with proper coupon parameters
   */
  buildCheckoutUrlWithAllCoupons(
    baseUrl: string,
    craftProducts?: CraftXGritProductEntry[],
    replaceCoupons?: string[],
    preComputedEnvironmentCoupons?: string[],
  ): string {
    if (!baseUrl) return baseUrl;

    const urlObj = new URL(this.toEnvCheckoutHost(baseUrl));
    const params = new URLSearchParams(urlObj.search);
    const existingVcValue = params.get('vc');
    const aggregatedCouponParam = this.couponQueryResolverService.setCouponQueryParam(CouponUseCases.CDI);

    const replaces = (replaceCoupons ?? []).map((c) => c?.trim()).filter((c): c is string => Boolean(c));
    const replaceMode = replaces.length > 0;

    let finalCoupons: string[];

    if (replaceMode) {
      // An active sale's banner coupon overwrites existing URL coupons and the
      // environment-coupon aggregation, but per-card Craft xgritCoupons still ride
      // through so each product's Offer coupon is preserved alongside the banner code.
      // The craft-spread below is load-bearing — removing it re-introduces EPS-3998.
      finalCoupons = [...new Set([...replaces, ...this.extractCraftCoupons(craftProducts)])];
    } else {
      // Merge mode: aggregate existing URL coupons with env + craft coupons.
      const existingHcValue = params.get('hc');
      const existingCouponValue = params.get('coupon');
      const existingCouponCodeValue = params.get('couponCode');

      let environmentCoupons: string[];
      if (preComputedEnvironmentCoupons !== undefined) {
        // Caller pre-aggregated env coupons — extract per-card craft inline (cheap)
        // and combine, skipping the page-scoped URL-param/cookie/banner walk.
        environmentCoupons = [...preComputedEnvironmentCoupons, ...this.extractCraftCoupons(craftProducts)];
      } else {
        const couponCollectionRequest = this.couponAggregationService.collectAllCoupons(craftProducts);
        environmentCoupons = this.couponAggregationService.getAllCouponCodes(couponCollectionRequest);
      }

      const allExistingCoupons: string[] = [];
      if (existingHcValue) {
        allExistingCoupons.push(
          ...existingHcValue
            .split(',')
            .map((c) => c.trim())
            .filter((c) => Boolean(c)),
        );
      }
      if (existingCouponValue) {
        allExistingCoupons.push(
          ...existingCouponValue
            .split(',')
            .map((c) => c.trim())
            .filter((c) => Boolean(c)),
        );
      }
      if (existingCouponCodeValue) {
        allExistingCoupons.push(
          ...existingCouponCodeValue
            .split(',')
            .map((c) => c.trim())
            .filter((c) => Boolean(c)),
        );
      }

      finalCoupons = [...new Set([...allExistingCoupons, ...environmentCoupons])];
    }

    // Always strip existing aggregated coupon params before re-adding the resolved set.
    params.delete('hc');
    params.delete('vc');
    params.delete('coupon');
    params.delete('couponCode');

    if (finalCoupons.length > 0) {
      params.set(aggregatedCouponParam, finalCoupons.join(','));
    }

    // Smart vc parameter handling (merge mode only — replace mode drops vc by design).
    if (!replaceMode && existingVcValue && !this.isCouponInAggregatedList(existingVcValue, finalCoupons)) {
      params.set('vc', existingVcValue);
    }

    urlObj.search = params.toString();
    return urlObj.toString();
  }

  /**
   * On dev and stage, points a link at the brand's prod checkout host to the environment's checkout
   * host instead. Craft content is shared across environments, so a stage entry can carry a prod
   * checkout URL, and non-prod must never hand off to prod checkout. Any other link, including a
   * relative one, is returned unchanged.
   */
  toEnvCheckoutHost(url: string): string {
    if (!this.envCheckoutHosts || !URL.canParse(url)) return url;
    const urlObj = new URL(url);
    if (urlObj.host !== this.envCheckoutHosts.prod) return url;
    urlObj.host = this.envCheckoutHosts.env;
    return urlObj.toString();
  }

  /**
   * Derives the prod checkout host from a non-prod one, by dropping its `ecom-{dev|stage}` label
   * (`checkout-insurance.ecom-stage.aceable.com` → `checkout-insurance.aceable.com`) or from
   * `NON_PROD_TO_PROD_CHECKOUT_HOST`. Returns null on prod, or for an unknown host, so no rewrite runs.
   */
  private resolveEnvCheckoutHosts(checkoutUrl: string): { env: string; prod: string } | null {
    // Local envs leave the URL empty, which `new URL` would reject.
    if (!URL.canParse(checkoutUrl)) return null;
    const env = new URL(checkoutUrl).host;
    const prod =
      NON_PROD_TO_PROD_CHECKOUT_HOST[env] ??
      (NON_PROD_HOST_LABEL.test(env) ? env.replace(NON_PROD_HOST_LABEL, '.') : null);
    return prod ? { env, prod } : null;
  }

  /**
   * Extracts and normalizes per-card Craft `xgritCoupon` codes (comma-separable,
   * trimmed, empty entries dropped) from the supplied product entries.
   */
  private extractCraftCoupons(craftProducts?: CraftXGritProductEntry[]): string[] {
    return (
      craftProducts?.flatMap((product) =>
        (product?.xgritCoupon ?? '')
          .split(',')
          .map((c) => c.trim())
          .filter((c) => Boolean(c)),
      ) ?? []
    );
  }

  /**
   * Checks if a coupon code is already included in the aggregated coupon list
   * @param couponCode - The coupon code to check
   * @param aggregatedCoupons - The list of aggregated coupon codes
   * @returns True if the coupon is already in the aggregated list
   */
  private isCouponInAggregatedList(couponCode: string, aggregatedCoupons: string[]): boolean {
    if (!couponCode || !aggregatedCoupons || aggregatedCoupons.length === 0) {
      return false;
    }

    const normalizedCoupon = couponCode.trim().toUpperCase();
    return aggregatedCoupons.some((aggregatedCoupon) => aggregatedCoupon.trim().toUpperCase() === normalizedCoupon);
  }

  /**
   * Legacy method that returns the original URL without vc parameter addition.
   * This replaces the GlobalCouponOverrideService.appendOverrideCouponToUrl method
   * to prevent direct vc parameter addition.
   *
   * @param url - The original checkout URL
   * @returns An object containing the unmodified URL and wasModified: false
   * @deprecated Use buildCheckoutUrlWithAllCoupons for proper coupon handling
   */
  appendOverrideCouponToUrl(url: string): { url: string; wasModified: boolean } {
    return { url, wasModified: false };
  }
}
